/**
 * PL Statement View Graph / Surveillance archive graph access for Backend-UI.
 * Archive → Agent-API `POST /v1/bet/bqGetBetsForSurv` (bet-service).
 */

import { agentBetFetch } from '@/api/client'
import {
  SURVEILLANCE_STATIC_ERRORS,
  mapGetBetsForSurveilliance,
  surveillanceCatchError,
  type CmGetBetsForSurveillianceResponse,
} from '@/api/map/surveillance'
import { validateStakeRange } from '@/features/surveillance/surveillanceFilters'
import type { SurveillanceGraphViewType } from '@/features/surveillance/graphViewType'
import type {
  GetGraphResult,
  RegenerateGraphResult,
  SurveillanceGraphFilter,
} from '@/features/surveillance/types'

async function httpGetGraph(filter: SurveillanceGraphFilter): Promise<GetGraphResult> {
  const stakeErr = validateStakeRange(filter.fromStake, filter.toStake)
  if (stakeErr) return { ok: false, message: stakeErr }
  if (!filter.eventType || !filter.eventId || !filter.marketId) {
    return { ok: false, message: 'Select sub game, event and market' }
  }

  const body: {
    eventType: string
    event: string
    market: string
    currencyType: string
    playerName: string[]
    fromStake?: number
    toStake?: number
  } = {
    eventType: filter.eventType,
    event: filter.eventId,
    market: filter.marketId,
    currencyType: filter.currencyType,
    playerName: filter.playerNames,
  }
  if (filter.fromStake != null) body.fromStake = filter.fromStake
  if (filter.toStake != null) body.toStake = filter.toStake

  try {
    // PL View Graph is archive-only (SurveillanceBeta BQ).
    const res = await agentBetFetch<CmGetBetsForSurveillianceResponse>(
      '/v1/bet/bqGetBetsForSurv',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return mapGetBetsForSurveilliance(res)
  } catch (err) {
    return {
      ok: false,
      message: surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.bets).message,
    }
  }
}

async function httpRegenerateGraph(
  marketId: string,
  graphType: SurveillanceGraphViewType = 'NORMAL',
  eventId?: string,
): Promise<RegenerateGraphResult> {
  const id = marketId.trim()
  if (!id) {
    return { ok: true, message: 'Unable to generate graph' }
  }
  const body: {
    marketId: string
    graphType: string
    eventId?: string
    wait: boolean
  } = {
    marketId: id,
    graphType,
    wait: true,
  }
  const event = (eventId ?? '').trim()
  if (event) body.eventId = event

  try {
    const res = await agentBetFetch<{
      message?: string
      data?: { msg?: string }
    }>('/v1/bet/uploadToBucketNew', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const msg =
      (typeof res.message === 'string' && res.message.trim()) ||
      (typeof res.data?.msg === 'string' && res.data.msg.trim()) ||
      'Graph generation started'
    return { ok: true, message: msg }
  } catch (err) {
    throw surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.regenerate)
  }
}

export const surveillanceClient = {
  getGraph(filter: SurveillanceGraphFilter): Promise<GetGraphResult> {
    return httpGetGraph(filter)
  },

  regenerateGraph(
    marketId: string,
    graphType: SurveillanceGraphViewType = 'NORMAL',
    eventId?: string,
  ): Promise<RegenerateGraphResult> {
    return httpRegenerateGraph(marketId, graphType, eventId)
  },
}
